import { ListRowButton } from '@/components/ui/list-row';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, CheckCheck, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { formatRelativeTime } from '@/lib/analyticsUtils';
import { cn } from '@/lib/utils';
import { useMarkNotificationsRead, useNotifications, type AppNotification } from '@/hooks/useNotifications';
import { notificationCategoryLabel as categoryLabel } from '@/lib/notificationLabels';
import { PREF_KEYS, usePreference } from '@/hooks/useUserPreferences';

const NotificationBell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: notifications = [], unreadCount, isError, isLoading, refetch } = useNotifications(30);
  const markRead = useMarkNotificationsRead();
  const hasUnread = (unreadCount ?? 0) > 0;
  const [soundOn, setSoundOn] = usePreference<boolean>(PREF_KEYS.notificationSound, true);

  // Brief pulse on the badge when the count goes up — visual twin of the chime.
  const prevCount = useRef<number | null>(null);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (unreadCount == null) return;
    if (prevCount.current != null && unreadCount > prevCount.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 3000);
      prevCount.current = unreadCount;
      return () => clearTimeout(t);
    }
    prevCount.current = unreadCount;
  }, [unreadCount]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next && hasUnread) {
      // Seen is enough — no extra click to clear the badge.
      markRead.mutate(undefined);
    }
  };

  const handleClick = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate([n.id]);
    // Reading it here is the same signal as tapping the push: silence it for the
    // rest of the team. Best-effort — never block navigation on it.
    void supabase.rpc('acknowledge_notification', { _notification_id: n.id });
    // Close first so the click visibly lands on the task.
    setOpen(false);
    const target = n.link_path || '/desk';
    if (target === location.pathname + location.search) {
      // Already here: refresh so the new item shows without a reload.
      void queryClient.invalidateQueries();
    } else {
      navigate(target);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 shrink-0"
          aria-label={
            isError
              ? 'Notifications — could not be loaded'
              : hasUnread
                ? `${unreadCount} unread notifications`
                : 'Notifications'
          }
        >
          <Bell className="w-4 h-4" />
          {isError && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1.05rem] h-[1.05rem] px-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold flex items-center justify-center">
              !
            </span>
          )}
          {!isError && hasUnread && pulse && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1.05rem] h-[1.05rem] rounded-full bg-destructive animate-ping" aria-hidden />
          )}
          {!isError && hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1.05rem] h-[1.05rem] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-semibold flex items-center justify-center">
              {(unreadCount ?? 0) > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 flex flex-col">
        <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={soundOn ? 'Turn notification sound off' : 'Turn notification sound on'}
              title={soundOn ? 'Sound on' : 'Sound off'}
              onClick={() => setSoundOn(!soundOn)}
            >
              {soundOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5 text-muted-foreground" />}
            </Button>
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => markRead.mutate(undefined)}
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>
        {isError ? (
          /* "Nothing new yet" on a failed fetch is a lie that quietly kills the trigger. */
          <div className="px-4 py-8 text-center">
            <WifiOff className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">We couldn't load your notifications</p>
            <p className="text-xs text-muted-foreground mt-1">
              This is a connection problem, not an empty inbox.
            </p>
            <Button variant="outline" size="sm" className="mt-3 h-7 text-xs" onClick={() => refetch()}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Try again
            </Button>
          </div>
        ) : isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Checking for updates…</div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Nothing new yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Watch a listing or follow a supplier and changes show up here.
            </p>
          </div>
        ) : (
          /* Radix ScrollArea clips here: its viewport is h-full, which a bare
             max-height never resolves, so the list was cropped with nothing to
             scroll. The popover already caps to the available height — just make
             this region the scrollable one. */
          <div className="overlay-scroll flex-1 min-h-0">
            <ul className="divide-y divide-border">
              {notifications.map((n) => (
                <li key={n.id}>
                  <ListRowButton
                    type="button"
                    onClick={() => handleClick(n)}
                    className={cn("block", cn(
                      'w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors',
                      !n.read_at && 'bg-primary/5',
                    ))}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && <span className="mt-1.5 circle-1.5 bg-primary flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-snug">{n.title}</p>
                        {n.body && (
                          <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {categoryLabel(n.category)} · {formatRelativeTime(n.created_at)}
                        </p>
                      </div>
                    </div>
                  </ListRowButton>
                </li>
              ))}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;